/**
 * klaviyo.js — Klaviyo onsite tracking utility for CAP B2C.
 *
 * Loads the Klaviyo onsite.js snippet (once) and exposes helpers
 * to identify users and track events.
 *
 * Only the PUBLIC API key is used here (client-side).
 * The private API key must never appear in frontend code.
 *
 * Usage:
 *   import { initKlaviyo, trackViewedProduct, trackAddedToCart, ... } from './klaviyo';
 *   initKlaviyo('YOUR_PUBLIC_KEY');
 *   trackViewedProduct({ ProductName: 'CAP Total Assist', ... });
 */

import config from '../../../config';

/* ============================== Event Names ============================== */

/**
 * Centralized event names — change here if Marketing confirms different names.
 */
export const KLAVIYO_EVENTS = {
  VIEWED_PRODUCT: 'Viewed Product',
  ADDED_TO_CART: 'Added to Cart',
  CHECKOUT_STARTED: 'Checkout Started',
};

/* ============================== Script Loader ============================== */

let _initialized = false;
let _publicKey = null;

/**
 * Inject the Klaviyo onsite.js script into the page (once).
 * Safe to call multiple times — subsequent calls are no-ops.
 *
 * @param {string} [publicKey] — Klaviyo public API key. Falls back to config.
 */
export function initKlaviyo(publicKey) {
  if (_initialized) return;

  const key = publicKey || config.klaviyoPublicApiKey;
  if (!key) {
    console.warn('[Klaviyo] No public API key configured — tracking disabled.');
    return;
  }

  _publicKey = key;

  try {
    // Avoid duplicate script injection
    if (document.querySelector('script[src*="klaviyo.com/onsite/js/klaviyo.js"]')) {
      _initialized = true;
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=' + key;
    document.head.appendChild(script);
    _initialized = true;
  } catch (e) {
    console.warn('[Klaviyo] Failed to load onsite script:', e);
  }
}

/* ============================== Helpers ============================== */

/**
 * Get the Klaviyo JS API object, waiting briefly for async load.
 * Returns null if unavailable.
 */
function getKlaviyoApi() {
  if (typeof window !== 'undefined' && window.klaviyo) {
    return window.klaviyo;
  }
  return null;
}

/**
 * Identify a user profile in Klaviyo.
 * Call this when you know the user's email (e.g. after email blur or login).
 *
 * @param {{ email: string, firstName?: string, lastName?: string }} profile
 */
export function identifyKlaviyoUser(profile) {
  if (!_initialized) return;

  try {
    const klaviyo = getKlaviyoApi();
    if (klaviyo && typeof klaviyo.identify === 'function') {
      const payload = {};
      if (profile.email) payload.email = profile.email;
      if (profile.firstName) payload.first_name = profile.firstName;
      if (profile.lastName) payload.last_name = profile.lastName;
      if (profile.phone) payload.phone_number = profile.phone;
      if (profile.properties) Object.assign(payload, profile.properties); // custom profile props — no $ prefix
      console.log('[Klaviyo] Identifying user:', payload);
      klaviyo.identify(payload);
    } else {
      // Klaviyo script may still be loading — queue via _learnq
      window._learnq = window._learnq || [];
      const identifyPayload = {};
      if (profile.email) identifyPayload.$email = profile.email;
      if (profile.firstName) identifyPayload.$first_name = profile.firstName;
      if (profile.lastName) identifyPayload.$last_name = profile.lastName;
      if (profile.phone) identifyPayload.$phone_number = profile.phone;
      if (profile.properties) Object.assign(identifyPayload, profile.properties); // custom profile props — no $ prefix
      console.log('[Klaviyo] Identifying user (queued):', identifyPayload);
      window._learnq.push(['identify', identifyPayload]);
    }
  } catch (e) {
    console.warn('[Klaviyo] identify error:', e);
  }
}

/**
 * Track a custom event in Klaviyo.
 * Non-blocking — errors are logged but never thrown.
 *
 * @param {string} eventName — One of KLAVIYO_EVENTS values.
 * @param {Object} properties — Event properties payload.
 */
export function trackKlaviyoEvent(eventName, properties) {
  if (!_initialized) return;

  try {
    const klaviyo = getKlaviyoApi();
    if (klaviyo && typeof klaviyo.track === 'function') {
      console.log('[Klaviyo] Tracking event:', eventName, properties || {});
      klaviyo.track(eventName, properties || {});
    } else {
      // Fallback to _learnq queue
      window._learnq = window._learnq || [];
      console.log('[Klaviyo] Tracking event (queued):', eventName, properties || {});
      window._learnq.push(['track', eventName, properties || {}]);
    }
  } catch (e) {
    console.warn('[Klaviyo] track error (' + eventName + '):', e);
  }
}

/* ============================== Per-Event Helpers ============================== */

/**
 * Build the standard property payload used across all events.
 *
 * @param {Object} data — Raw data from the Quote component.
 * @returns {Object} Normalized properties.
 */
function buildBaseProperties(data) {
  const props = {
    ProductID: data.productId || null,
    ProductName: data.planName || null,
    Destination: data.destination || null,
    Resident: data.residency || null,
    Amount: data.amount || 0,
    Currency: data.currency || 'USD',
    StartDate: data.startDate || null,
    EndDate: data.endDate || null,
  };
  if (data.email) props.$email = data.email;
  if (data.firstName) props.$first_name = data.firstName;
  if (data.lastName) props.$last_name = data.lastName;
  if (data.membershipNumber) props.MembershipNumber = data.membershipNumber;
  return props;
}

/**
 * Track "Viewed Product" — fires when the user views the plan/pricing page (step 2).
 */
export function trackViewedProduct(data) {
  const props = buildBaseProperties(data);
  delete props.Amount; // View Product shouldn't send price, even if default is 0
  trackKlaviyoEvent(KLAVIYO_EVENTS.VIEWED_PRODUCT, props);
}

/**
 * Track "Added to Cart" — fires when the user selects a plan and continues (step 2 → 3).
 */
export function trackAddedToCart(data) {
  const props = buildBaseProperties(data);
  // Raw quote param (the base64 blob the marketing site uses to carry trip
  // details into /quote) — passed through exactly as received, unmodified.
  if (data.quoteParam) props.QuoteParam = data.quoteParam;
  trackKlaviyoEvent(KLAVIYO_EVENTS.ADDED_TO_CART, props);
}

/**
 * Track "Checkout Started" — fires when the payment page loads (step 3).
 */
export function trackCheckoutStarted(data) {
  if (data.email) {
    identifyKlaviyoUser({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      properties: {
        start_date: data.startDate || null, // "YYYY-MM-DD" so Klaviyo types it as a date
        end_date: data.endDate || null,
      },
    });
  }

  const props = buildBaseProperties(data);
  if (data.quoteId) props.QuoteID = data.quoteId;
  if (data.paymentStatus) props.PaymentStatus = data.paymentStatus;
  trackKlaviyoEvent(KLAVIYO_EVENTS.CHECKOUT_STARTED, props);
}
